import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import { type IAdminCategory } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Button, Col, Form, Modal, Row, Select, Skeleton } from 'antd';
import { type DefaultOptionType } from 'antd/lib/select';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { getFilterSort } from '../../fragments/select-filter';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import {
  categorySaveChildCategories,
  categorySavePosition,
} from '../../reducers/category/reducer';
import Link from '../link';
import { type ISortableListItem } from '../sortable-list';
import { SortableList } from '../sortable-list';

interface CategoryLocalized {
  uuid: string;
  name: string;
}

interface IForm {
  childCategoriesUUIDs: string[];
}

function ItemTemplate({
  record,
}: {
  record: ISortableListItem;
}): React.ReactElement {
  return <div>{record.name}</div>;
}

function ChildCategoriesForm({
  categoriesLocalized,
  categoryUUID,
}: {
  categoriesLocalized: CategoryLocalized[];
  categoryUUID: string | undefined;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const category = useSelector((s: RootState) => s.Category.category);
  const categoryInProcess = useSelector((s: RootState) => s.Category.inProcess);

  const [form] = Form.useForm<IForm>();

  const onFinish = useCallback(
    ({ childCategoriesUUIDs }: IForm): void => {
      if (!category) {
        return;
      }
      dispatch(
        categorySaveChildCategories(category.uuid, childCategoriesUUIDs),
      );
    },
    [category, dispatch],
  );

  return (
    <Form form={form} key={categoryUUID} onFinish={onFinish} layout="vertical">
      <Form.Item
        name="childCategoriesUUIDs"
        rules={[
          {
            required: true,
            message: 'Please select child categories',
          },
        ]}
      >
        <Select
          mode="multiple"
          allowClear
          placeholder="Select child categories"
          disabled={categoryInProcess}
          showSearch={{
            autoClearSearchValue: false,
            filterOption: (
              input: string,
              option: DefaultOptionType | undefined,
            ): boolean => {
              const labelText: string = String(
                option ? option.label : '',
              ).toLowerCase();
              return labelText.includes(input.toLowerCase());
            },
            filterSort: getFilterSort,
          }}
          options={categoriesLocalized.map(
            (cat: CategoryLocalized): DefaultOptionType => ({
              value: cat.uuid,
              label: cat.name,
            }),
          )}
        />
      </Form.Item>

      <Button type="primary" htmlType="submit">
        Submit
      </Button>
    </Form>
  );
}

function ChildCategoriesComponent(): React.ReactElement {
  const dispatch = useAppDispatch();
  const categoriesAll = useSelector((s: RootState) => s.Categories.categories);
  const category = useSelector((s: RootState) => s.Category.category);
  const categoryUpdatedAt = useSelector((s: RootState) => s.Category.updatedAt);
  const languageType = useSelector((s: RootState) => s.Language.languageType);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const categoriesLocalized = useMemo<
    CategoryLocalized[]
  >((): CategoryLocalized[] => {
    if (!categoriesAll) {
      return [];
    }

    return categoriesAll
      .filter(
        (cat: IAdminCategory): boolean =>
          !!category &&
          category.uuid !== cat.uuid &&
          !category.childCategoriesUUIDs.includes(cat.uuid),
      )
      .sort(
        (a: IAdminCategory, b: IAdminCategory): number =>
          a.position - b.position,
      )
      .map((cat: IAdminCategory): CategoryLocalized => ({
        uuid: cat.uuid,
        name: getTranslation(cat.names, languageType) || EMPTY_VALUE,
      }));
  }, [categoriesAll, category, languageType]);

  const sortedChildCategories = useMemo<
    IAdminCategory[]
  >((): IAdminCategory[] => {
    if (!category?.childCategoriesUUIDs.length) {
      return [];
    }

    return category.childCategoriesUUIDs
      .map((uuid: string): IAdminCategory | undefined =>
        categoriesAll?.find((c: IAdminCategory): boolean => c.uuid === uuid),
      )
      .filter(notEmpty)
      .sort(
        (a: IAdminCategory, b: IAdminCategory): number =>
          a.position - b.position,
      );
  }, [
    category?.uuid,
    category?.childCategoriesUUIDs,
    categoriesAll,
    categoryUpdatedAt,
  ]);

  const dataSourceSortableList = useMemo<ISortableListItem[]>(
    (): ISortableListItem[] =>
      sortedChildCategories.map((cat: IAdminCategory): ISortableListItem => ({
        id: cat.uuid,
        uuid: cat.uuid,
        name: getTranslation(cat.names, languageType) || EMPTY_VALUE,
        position: cat.position,
      })),
    [sortedChildCategories, languageType],
  );

  return (
    <Row>
      <Col xs={24} md={8}>
        <ChildCategoriesForm
          categoriesLocalized={categoriesLocalized}
          categoryUUID={category?.uuid}
        />
      </Col>

      <Col span={15} offset={1}>
        {sortedChildCategories.length === 0 &&
        !category?.childCategoriesUUIDs.length ? (
          <Skeleton active />
        ) : (
          <div>
            {sortedChildCategories.map(
              (cat: IAdminCategory): React.ReactElement => (
                <div key={cat.uuid}>
                  <Link
                    to={`/category/${cat.uuid}`}
                    label={
                      getTranslation(cat.names, languageType) || EMPTY_VALUE
                    }
                    openInNewTab
                  />
                </div>
              ),
            )}
          </div>
        )}

        {dataSourceSortableList.length > 0 && (
          <>
            <Button type="primary" onClick={(): void => setIsModalOpen(true)}>
              Change categories order
            </Button>

            <Modal
              title="Change categories order"
              open={isModalOpen}
              onCancel={(): void => setIsModalOpen(false)}
              footer={null}
            >
              <SortableList
                records={dataSourceSortableList}
                id="categories-sortable-list"
                ItemTemplate={ItemTemplate}
                saveCallback={(records: ISortableListItem[]): void => {
                  records.forEach((record: ISortableListItem): void => {
                    dispatch(
                      categorySavePosition(record.uuid, record.position),
                    );
                  });
                  setIsModalOpen(false);
                }}
              />
            </Modal>
          </>
        )}
      </Col>
    </Row>
  );
}

export default ChildCategoriesComponent;
